import { useMutation, useQuery } from '@tanstack/react-query';
import { notification } from 'antd';
import { APIIntegrationImPACT } from 'lib/api/firebase/apis/api-integration-impact';
import { queryKeyFactory } from 'lib/utils/queryKeyFactory';

export function useImpactPilotMutations() {
  const joinPilot = useMutation({
    mutationFn: APIIntegrationImPACT.joinPilot,
    onSuccess: () => {
      notification.open({
        type: 'success',
        message: 'Successfully joined the pilot program!'
      });
    },
    onError: (error: Error) => {
      notification.open({
        type: 'error',
        message: `Failed to join the pilot program: ${error.message}`
      });
    }
  });

  const leavePilot = useMutation({
    mutationFn: APIIntegrationImPACT.leavePilot,
    onSuccess: () => {
      notification.open({
        type: 'success',
        message: 'Successfully left the pilot program!'
      });
    },
    onError: (error: Error) => {
      notification.open({
        type: 'error',
        message: `Failed to leave the pilot program: ${error.message}`
      });
    }
  });

  const addPatient = useMutation({
    mutationFn: APIIntegrationImPACT.addPatient,
    onSuccess: () => {
      notification.open({
        type: 'success',
        message: 'Successfully added the test-taker'
      });
    },
    onError: (error: Error) => {
      notification.open({
        type: 'error',
        message: `Failed to add the test-taker: ${error.message}`
      });
    }
  });
  const generateFakeToken = useMutation({
    mutationFn: APIIntegrationImPACT.generateFakeToken
  });

  const decodeToken = useMutation({
    mutationFn: APIIntegrationImPACT.decodeToken
  });

  return { joinPilot, leavePilot, addPatient, generateFakeToken, decodeToken };
}

/**
 * Check if a token encrypted by ImPACT is valid.
 *
 * This does not require the user to be authenticated.
 *
 * @param token
 * @returns
 */
export function useIsTokenValid(token: string | null) {
  return useQuery({
    queryKey: queryKeyFactory.integration.impact.isTokenValid(token ?? ''),
    enabled: !!token,
    queryFn: () => APIIntegrationImPACT.isTokenValid({ token: token! })
  });
}

export function useDecodeToken(token: string | null) {
  return useQuery({
    queryKey: queryKeyFactory.integration.impact.decodedToken(token ?? ''),
    enabled: !!token,
    queryFn: () =>
      APIIntegrationImPACT.decodeToken({ token: token! }).then(
        _ => _.data.output
      )
  });
}

/**
 * Decodes the token from ImPACT then immediately checks if the user is assigned to the patient
 * @param token
 * @returns
 */
export function useImpactPilotDecodeAndCheckUseOfToken(
  token: string | null,
  locationToken: string | null
) {
  const locationCodeQuery = useDecodeToken(locationToken);
  const tokenQuery = useDecodeToken(token);

  return useQuery({
    queryKey: queryKeyFactory.integration.impact.decodedToken(
      token ? `${token}-and-use` : ''
    ),
    queryFn: async () => ({
      assignmentDetails:
        await APIIntegrationImPACT.isAssignedToPatientWithPassportId({
          passportId: tokenQuery.data ?? '',
          locationCode: locationCodeQuery.data ?? ''
        }).then(_ => _.data),
      passportId: tokenQuery.data ?? ''
    }),

    enabled: !!tokenQuery.data && !!locationCodeQuery.data
  });
}

export function useIsAssignedToPatientWithPassportId(
  passportId: string,
  locationCode: string
) {
  return useQuery({
    queryKey:
      queryKeyFactory.integration.impact.isAssignedToPatientWithPassportId(
        passportId
      ),
    queryFn: () =>
      APIIntegrationImPACT.isAssignedToPatientWithPassportId({
        passportId,
        locationCode
      }),
    enabled: typeof passportId === 'string' && passportId.length > 0
  });
}
